import { useContext, useEffect, useState } from "react";
import { Menu, MenuItem, Tooltip, ListItemText } from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckIcon from "@mui/icons-material/Check";
import toast from "react-hot-toast";

import { AuthContext } from "../../context/auth-context";
import { ClinicaContext } from "../../context/clinica-context";
import { useHttpClient } from "../../hooks/http-hook";
import "./SeletorUnidade.scss";

/**
 * Indicador da unidade ativa, com troca sem sair da sessão.
 *
 * Não é decoração: enquanto uma clínica tiver duas unidades, o utilizador tem
 * de conseguir ver, a qualquer momento, sob que LED está a faturar.
 *
 * Só aparece nas clínicas com `multiEstabelecimento` ligado — nas restantes
 * não há nada a escolher e o header fica exatamente como estava.
 */
const SeletorUnidade = () => {
  const auth = useContext(AuthContext);
  const { clinica } = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [aTrocar, setATrocar] = useState(false);

  // `clinica` aqui JÁ É a clínica: o valor do contexto é
  // `{ clinica, configuracoes, setConfiguracoes }` e este ficheiro
  // desestrutura-o. Quem faz `useContext(ClinicaContext)` sem desestruturar —
  // o BadgeUnidade, por exemplo — é que precisa do `clinica.clinica`.
  //
  // Escrever `clinica?.clinica` aqui dava undefined, o `multi` ficava falso e o
  // seletor devolvia `null`: a unidade da sessão não aparecia em lado nenhum,
  // exatamente quando havia duas para escolher.
  const clinicaId = clinica?._id;
  const multi = !!clinica?.multiEstabelecimento;

  // Mostra o aviso deixado antes do recarregamento. Tem de estar acima do
  // `return null` porque os hooks não podem correr condicionalmente.
  useEffect(() => {
    const nome = sessionStorage.getItem("unidadeTrocadaPara");
    if (!nome) return;
    sessionStorage.removeItem("unidadeTrocadaPara");
    toast.success(`Agora está em ${nome}`);
  }, []);

  if (!multi || !auth.estabelecimento) return null;

  const abrir = async (event) => {
    setAnchorEl(event.currentTarget);

    if (unidades.length > 0) return;

    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setUnidades(resposta.estabelecimentos || []);
    } catch (err) {
      console.error("Erro ao buscar unidades:", err);
    }
  };

  const trocar = async (unidade) => {
    if (unidade._id === auth.estabelecimento._id) {
      setAnchorEl(null);
      return;
    }

    setATrocar(true);
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/users/trocar-estabelecimento`,
        "POST",
        JSON.stringify({ estabelecimentoId: unidade._id }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      auth.atualizarEstabelecimento(resposta.token, resposta.estabelecimento);
      setAnchorEl(null);

      // O aviso fica guardado para ser mostrado DEPOIS do recarregamento.
      // Mostrá-lo aqui era inútil: o `reload()` logo a seguir matava-o antes de
      // se ler. E é do outro lado que a frase passa a ser verdade.
      sessionStorage.setItem(
        "unidadeTrocadaPara",
        resposta.estabelecimento.nome,
      );

      // Recarregar: os ecrãs abertos foram carregados com as caixas e os dados
      // da unidade anterior e não têm forma de saber que a sessão mudou. Ficar
      // com meia aplicação a mostrar a unidade errada é pior do que o salto.
      window.location.reload();
    } catch (err) {
      /* A falha era só para a consola. Do lado de quem usa, uma troca recusada
         (unidade inativa, ou não associada ao utilizador) era indistinguível de
         um clique que não pegou: o menu fechava e ficava tudo na mesma. Sem
         mensagem não há forma de saber que o servidor recusou, nem porquê. */
      console.error("Erro ao trocar de unidade:", err);
      toast.error(err?.message || "Não foi possível trocar de unidade.");
    } finally {
      setATrocar(false);
    }
  };

  return (
    <>
      <Tooltip title={`Unidade: ${auth.estabelecimento.nome} — clique para trocar`}>
        <button type="button" className="seletor-unidade" onClick={abrir}>
          <StorefrontIcon className="seletor-unidade__icone" />
          <span className="seletor-unidade__codigo">
            {auth.estabelecimento.codigo}
          </span>
        </button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {unidades.length === 0 && (
          <MenuItem disabled>A carregar unidades…</MenuItem>
        )}
        {unidades.map((u) => {
          const ativa = u._id === auth.estabelecimento._id;
          return (
            <MenuItem
              key={u._id}
              selected={ativa}
              disabled={aTrocar}
              onClick={() => trocar(u)}
            >
              <ListItemText
                primary={`${u.codigo} — ${u.nome}`}
                secondary={u.cidade || u.endereco || null}
              />
              {ativa && (
                <CheckIcon fontSize="small" className="seletor-unidade__check" />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default SeletorUnidade;
